import React, { useMemo } from 'react';
import { useFormState, type FieldErrors } from 'react-hook-form';
import { Typography } from '@packages/ui/primitives';
import { Inline, Stack, Surface } from '@packages/ui/layout';
import { FormGrid } from '@packages/ui/patterns';
import { rem } from '@packages/ui/tokens';
import type { FormEachNode } from '../../types/layout';
import { FormRowExcludedValuesProvider } from '../../context/FormRowContext';
import { useFormEachArray } from '../../hooks/useFormEachArray';
import { resolveAddButtonPosition } from '../../utils/resolveAddButtonPosition';
import { buildExcludedSiblingValues } from './each/buildExcludedSiblingValues';
import { FormEachAddButton } from './each/FormEachAddButton';
import { FormEachRemoveButton } from './each/FormEachRemoveButton';
import { FormEachResetButton } from './each/FormEachResetButton';
import { FormEachRowActionsButton } from './each/FormEachRowActionsButton';
import { FormEachRowHeader } from './each/FormEachRowHeader';
import { FormFieldAdapter } from './FormFieldAdapter';
import { FormRowProvider } from '../../context/FormRowContext';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { useEffectProcessor } from '../../processors/effectProcessor';
import { useObserverProcessor } from '../../processors/observerProcessor';
import { useRowObserverProcessor } from '../../processors/rowObserverProcessor';
import { useArrayEffectProcessor } from '../../processors/arrayEffectProcessor';
import { prefixEffects, prefixObservers } from '../../processors/pathRewriter';
import { resolveItemTokens } from '../../resolver/tokenResolver';
import type { FormFieldMapping } from '../../types/fieldMapping';
import { evaluateCondition } from '../../evaluators/conditionEvaluator';

const TABLE_SCROLL_MAX_HEIGHT = 375;
const ROW_INDEX_COLUMN_MIN_WIDTH = 32;
const ACTIONS_COLUMN_MIN_WIDTH = 56;
const ROW_PADDING = `${rem(15)} ${rem(20)}`;
const COLUMN_GAP = rem(16);

const tableRowGridStyle: React.CSSProperties = {
  alignItems: 'start',
};

/** Keep delete/reset icons on the input row — not vertically centered in the error-expanded row. */
const tableActionsCellStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  alignSelf: 'start',
  minHeight: rem(40),
};

function rowHasValidationErrors(
  errors: FieldErrors,
  arrayName: string,
  index: number,
): boolean {
  const arrayErrors = errors[arrayName];
  if (!Array.isArray(arrayErrors)) return false;

  const rowErrors = arrayErrors[index];
  if (rowErrors == null || typeof rowErrors !== 'object') return false;

  return Object.values(rowErrors as Record<string, unknown>).some((value) => value != null);
}

interface FormEachBandedTableRendererProps {
  node: FormEachNode;
}

function buildTableGridTemplate(
  node: FormEachNode,
  options: { showRowIndexColumn: boolean; showActionsCell: boolean },
): string {
  const colDefs: string[] = [];

  if (options.showRowIndexColumn) {
    colDefs.push(`${ROW_INDEX_COLUMN_MIN_WIDTH}px`);
  }

  for (const field of node.itemFields) {
    const min = field.matrix?.minWidthPx ?? 120;
    const max = field.matrix?.maxWidthPx;
    const maxTrack = max != null ? `${max}px` : '1fr';
    colDefs.push(`minmax(${min}px, ${maxTrack})`);
  }

  if (options.showActionsCell) {
    colDefs.push(`minmax(40px, ${ACTIONS_COLUMN_MIN_WIDTH}px)`);
  }

  return colDefs.join(' ');
}

function TableColumnHeader({
  label,
  required,
  showRequired,
  align = 'left',
}: {
  label: string;
  required?: boolean;
  showRequired: boolean;
  align?: 'left' | 'center';
}): React.ReactElement {
  return (
    <Typography
      variant='body-xs'
      as='div'
      style={{
        fontWeight: 600,
        textAlign: align,
      }}>
      {label}
      {required && showRequired ? (
        <span
          aria-hidden='true'
          style={{
            marginLeft: rem(4),
            color: 'var(--ui-status-danger)',
            fontWeight: 600,
          }}>
          *
        </span>
      ) : null}
    </Typography>
  );
}

function TableHeaderGrid({
  node,
  tableGridTemplate,
  showRowIndexColumn,
  showActionsCell,
  actionsColumnLabel,
  showRequiredInHeaders,
}: {
  node: FormEachNode;
  tableGridTemplate: string;
  showRowIndexColumn: boolean;
  showActionsCell: boolean;
  actionsColumnLabel: string;
  showRequiredInHeaders: boolean;
}): React.ReactElement {
  return (
    <FormGrid columns={tableGridTemplate} columnGap={COLUMN_GAP} collapse='sm'>
      {showRowIndexColumn && (
        <TableColumnHeader label='#' showRequired={false} align='center' />
      )}
      {node.itemFields.map((field) => (
        <TableColumnHeader
          key={`header_${field.name}`}
          label={field.def.label}
          required={field.required}
          showRequired={showRequiredInHeaders}
        />
      ))}
      {showActionsCell && (
        <TableColumnHeader
          label={actionsColumnLabel}
          showRequired={false}
          align='center'
        />
      )}
    </FormGrid>
  );
}

export const FormEachBandedTableRenderer = React.memo(function FormEachBandedTableRenderer({
  node,
}: FormEachBandedTableRendererProps): React.ReactElement {
  const { form, hydrationEpochRef } = useFormEngineForm();
  const { meta, getOptionProp, isReadOnly } = useFormEngineState();
  const {
    fields,
    handleAdd,
    handleRemove,
    showAdd,
    canRemoveRow,
    allRowValues,
    siblingExcludeFields,
  } = useFormEachArray(node);

  useArrayEffectProcessor({
    form: form as never,
    arrayEffects: node.arrayEffects,
    arrayName: node.name,
    meta,
    getOptionProp,
    hydrationEpochRef,
    itemRules: node.itemRules,
    revalidateOnPerRowSync: node.revalidateOnPerRowSync,
    revalidateTargetsOnPerRowSync: node.revalidateTargetsOnPerRowSync,
  });

  const hasRowActions = (node.rowActions ?? []).length > 0;

  const addPosition = resolveAddButtonPosition(node);
  const addControl = showAdd ? <FormEachAddButton node={node} onClick={handleAdd} /> : null;
  const showHeaderRow = true;
  const showRemove = canRemoveRow();
  const showReset = useMemo(() => {
    if (node.resetButton == null || isReadOnly) return false;
    const vw = node.resetButton.visibleWhen;
    if (vw == null) return true;
    const currentValues = form.getValues() as Record<string, unknown>;
    return evaluateCondition(vw, { values: currentValues, meta, getOptionProp });
  }, [node.resetButton, isReadOnly, fields.length, form, meta, getOptionProp]);

  const showRowIndexColumn =
    node.rowHeader?.style != null && node.rowHeader.style !== 'none';
  const showActionsCell = showRemove || showReset || hasRowActions;
  const actionsColumnLabel = node.actionsColumnLabel ?? 'Actions';

  const tableGridTemplate = useMemo(
    () =>
      buildTableGridTemplate(node, {
        showRowIndexColumn,
        showActionsCell,
      }),
    [node, showRowIndexColumn, showActionsCell],
  );

  const totalMinWidth = useMemo(() => {
    const indexWidth = showRowIndexColumn ? ROW_INDEX_COLUMN_MIN_WIDTH : 0;
    const fieldWidths = node.itemFields.reduce(
      (sum, f) => sum + (f.matrix?.minWidthPx ?? 120),
      0,
    );
    const actionWidth = showActionsCell ? ACTIONS_COLUMN_MIN_WIDTH : 0;
    const gapCount =
      node.itemFields.length +
      (showRowIndexColumn ? 1 : 0) +
      (showActionsCell ? 1 : 0) -
      1;
    const gapWidth = Math.max(0, gapCount) * 16;
    return indexWidth + fieldWidths + actionWidth + gapWidth;
  }, [node.itemFields, showRowIndexColumn, showActionsCell]);

  const hasInlineHeader = addPosition === 'top-right' && (node.title != null || addControl != null);
  const scrollable = fields.length > 4;
  const showRequiredInHeaders = fields.length > 0;

  return (
    <Stack gap='var(--ui-space-md, 16px)' style={{ marginTop: rem(8) }}>
      {hasInlineHeader && (
        <Inline align='center' justify='between' wrap gap='var(--ui-space-sm, 8px)'>
          {node.title != null && <Typography variant='h5'>{node.title}</Typography>}
          {addControl}
        </Inline>
      )}

      <Surface
        bordered
        padding='0'
        semanticId={`${node.name}EachTable`}
        style={{ overflow: 'hidden' }}>
        <Surface
          padding='0'
          style={{
            maxHeight: rem(TABLE_SCROLL_MAX_HEIGHT),
            overflowY: scrollable ? 'auto' : 'visible',
            overflowX: 'auto',
            minWidth: totalMinWidth,
          }}>
          {showHeaderRow && (
            <Surface
              variant='inset'
              padding={ROW_PADDING}
              data-testid='each-table-header-banded'
              style={{
                border: 'none',
                borderRadius: `${rem(6)} ${rem(6)} 0 0`,
                borderBottom: `${rem(1)} solid var(--ui-border-muted)`,
              }}>
              <TableHeaderGrid
                node={node}
                tableGridTemplate={tableGridTemplate}
                showRowIndexColumn={showRowIndexColumn}
                showActionsCell={showActionsCell}
                actionsColumnLabel={actionsColumnLabel}
                showRequiredInHeaders={showRequiredInHeaders}
              />
            </Surface>
          )}

          {fields.length === 0 && node.emptyState != null && (
            <Surface padding={ROW_PADDING}>
              <Typography variant='body-s'>{node.emptyState.title}</Typography>
              {node.emptyState.description != null && (
                <Typography variant='body-xs' color='var(--ui-text-muted)'>
                  {node.emptyState.description}
                </Typography>
              )}
            </Surface>
          )}

          {fields.map((item, index) => (
            <BandedTableEachRow
              key={item.id}
              node={node}
              index={index}
              total={fields.length}
              gridTemplate={tableGridTemplate}
              showRowIndexColumn={showRowIndexColumn}
              showRemove={showRemove}
              showActionsCell={showActionsCell}
              hasRowActions={hasRowActions}
              suppressCellLabels={showHeaderRow}
              isLastRow={index === fields.length - 1}
              excludedValues={buildExcludedSiblingValues(allRowValues, index, siblingExcludeFields)}
              rowValues={allRowValues?.[index] as Record<string, unknown> | undefined}
              onRemove={handleRemove}
            />
          ))}
        </Surface>
      </Surface>

      {!hasInlineHeader && addControl}
    </Stack>
  );
});

interface BandedTableEachRowProps {
  node: FormEachNode;
  index: number;
  total: number;
  gridTemplate: string;
  showRowIndexColumn: boolean;
  showRemove: boolean;
  showActionsCell: boolean;
  hasRowActions: boolean;
  suppressCellLabels: boolean;
  isLastRow: boolean;
  excludedValues: Record<string, unknown[]>;
  rowValues?: Record<string, unknown>;
  onRemove: (index: number) => void;
}

const BandedTableEachRow = React.memo(function BandedTableEachRow({
  node,
  index,
  total,
  gridTemplate,
  showRowIndexColumn,
  showRemove,
  showActionsCell,
  hasRowActions,
  suppressCellLabels,
  isLastRow,
  excludedValues,
  rowValues,
  onRemove,
}: BandedTableEachRowProps): React.ReactElement {
  const { form, programmaticWriteGuard } = useFormEngineForm();
  const { meta, getOptionProp } = useFormEngineState();
  const namePrefix = `${node.name}.${index}`;

  const rowMeta = useMemo(
    () => ({
      index,
      total,
      isFirst: index === 0,
      isLast: index === total - 1,
      documentCategory: node.documentCategory,
      tablePresentation: suppressCellLabels,
    }),
    [index, total, node.documentCategory, suppressCellLabels],
  );

  const resolvedItemFields = useMemo(() => {
    return node.itemFields.map((field) =>
      resolveItemTokens(field, namePrefix) as FormFieldMapping,
    );
  }, [node.itemFields, namePrefix]);

  const scopedEffects = useMemo(
    () => (node.itemEffects?.length ? prefixEffects(node.itemEffects, namePrefix) : []),
    [node.itemEffects, namePrefix],
  );

  const scopedObservers = useMemo(
    () => (node.itemObservers?.length ? prefixObservers(node.itemObservers, namePrefix) : []),
    [node.itemObservers, namePrefix],
  );

  useEffectProcessor({
    form: form as never,
    effects: scopedEffects,
    meta,
    getOptionProp,
    programmaticWriteGuard,
  });
  useObserverProcessor({ form: form as never, observers: scopedObservers, meta, getOptionProp });
  useRowObserverProcessor({
    form: form as never,
    observers: node.itemRowObservers ?? [],
    rowPrefix: namePrefix,
    meta,
    getOptionProp,
  });

  const { errors } = useFormState({ control: form.control });
  const alignActionsToRowHeight = rowHasValidationErrors(errors, node.name, index);

  const actionsControl = (
    <Inline align='center' justify='center' gap='var(--ui-space-xs, 4px)'>
      {node.resetButton != null && (
        <FormEachResetButton node={node} index={index} arrayLength={total} />
      )}
      {showRemove && <FormEachRemoveButton node={node} index={index} onRemove={onRemove} />}
      {hasRowActions && (
        <FormEachRowActionsButton node={node} index={index} rowValues={rowValues} />
      )}
    </Inline>
  );

  return (
    <FormRowExcludedValuesProvider value={excludedValues}>
      <FormRowProvider value={rowMeta}>
        <Surface
          padding={ROW_PADDING}
          style={{
            border: 'none',
            borderRadius: isLastRow ? `0 0 ${rem(6)} ${rem(6)}` : undefined,
            borderBottom: isLastRow ? undefined : `${rem(1)} solid var(--ui-border-muted)`,
          }}>
          <FormGrid
            columns={gridTemplate}
            columnGap={COLUMN_GAP}
            collapse='sm'
            style={alignActionsToRowHeight ? tableRowGridStyle : undefined}>
            {showRowIndexColumn && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <FormEachRowHeader config={node.rowHeader!} index={index} rowValues={rowValues} />
              </div>
            )}
            {resolvedItemFields.map((field, fieldIndex) => (
              <div key={field.name || `__table_row_${fieldIndex}`} style={{ minWidth: 0 }}>
                <FormFieldAdapter field={field} namePrefix={namePrefix} />
              </div>
            ))}
            {showActionsCell &&
              (alignActionsToRowHeight ? (
                <div style={tableActionsCellStyle} data-testid='each-table-actions-cell'>
                  {actionsControl}
                </div>
              ) : (
                actionsControl
              ))}
          </FormGrid>
        </Surface>
      </FormRowProvider>
    </FormRowExcludedValuesProvider>
  );
});
